/**
 * "This app has moved" — a blocking overlay with a countdown to the new home.
 *
 * Switched on entirely from the backend (`APP_RELOCATED_URL`), never from the bundle: Create React
 * App bakes REACT_APP_* at build time, so a frontend flag could only be revoked by rebuilding and
 * redeploying. This asks the server on mount instead, which makes turning it off a one-line env
 * change and a restart. For a change that deliberately makes the product unusable, how fast you
 * can UNDO it is the property that matters most.
 *
 * THREE DELIBERATE BEHAVIOURS:
 *
 * 1. FAILS OPEN. If the status call errors, no overlay. A network blip must not lock everyone out
 *    of a working app, and if the backend is truly down the old app is useless anyway. The overlay
 *    appears only on an explicit, positive signal.
 *
 * 2. ESCAPE HATCH, UNADVERTISED. `?stay=1` suppresses it for the tab (sessionStorage). Not a
 *    visible button — a "stay here" link on screen would defeat the point — but without any way
 *    back in, a problem at the new host would leave nobody able to reach the old one. Deliberately
 *    findable only by someone who already knows.
 *
 * 3. BLOCKS, DOESN'T SHUT DOWN. This stops people USING the app; it does not stop a direct API
 *    call or a stale script. That is the honest scope, and it is why revoking can never lock the
 *    administrator out.
 */
import React, { useEffect, useRef, useState } from 'react';

const STAY_KEY = 'keycrew-stay-on-old-app';
const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const RelocationNotice = () => {
  const [status, setStatus] = useState(null);   // { relocated, url, countdown }
  const [remaining, setRemaining] = useState(null);
  const redirected = useRef(false);

  // Honour ?stay=1 before anything else, and remember it for the tab so the escape hatch survives
  // navigation within the app.
  const staying = (() => {
    try {
      if (new URLSearchParams(window.location.search).get('stay') === '1') {
        sessionStorage.setItem(STAY_KEY, '1');
      }
      return sessionStorage.getItem(STAY_KEY) === '1';
    } catch { return false; }
  })();

  useEffect(() => {
    if (staying) return;
    let cancelled = false;
    fetch(`${backendUrl}/api/app-status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => {
        if (cancelled || !d?.relocated || !d.url) return;
        setStatus(d);
        setRemaining(d.countdown);
      })
      .catch(() => { /* fail open — see the note above */ });
    return () => { cancelled = true; };
  }, [staying]);

  // Countdown, then go. `replace` rather than `assign` so Back doesn't bounce the user straight
  // into the overlay again.
  useEffect(() => {
    if (remaining === null || !status?.url) return;
    if (remaining <= 0) {
      if (!redirected.current) {
        redirected.current = true;
        window.location.replace(status.url);
      }
      return;
    }
    const t = setTimeout(() => setRemaining((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining, status]);

  if (staying || !status?.relocated) return null;

  return (
    // Fixed, full-viewport, above everything. `inset-0` plus a high z-index means nothing behind
    // it is reachable by mouse or keyboard — the app is visible but inert, which is the point:
    // people should recognise where they were and understand it has moved, not face a blank page.
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm px-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="relocation-title"
      data-testid="relocation-overlay"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-kc-blue/10 flex items-center justify-center mx-auto mb-5">
          <svg className="w-6 h-6 text-kc-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
              d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>

        <h1 id="relocation-title" className="text-2xl font-bold text-kc-navy mb-3">
          This App Has Moved.
        </h1>

        <p className="text-sm text-slate-600 mb-2">You Can Access it at</p>
        <a
          href={status.url}
          className="block text-lg font-semibold text-kc-blue hover:underline break-all mb-6"
          data-testid="relocation-link"
        >
          {status.url}
        </a>

        <p className="text-sm text-slate-500">
          You will be automatically redirected in{' '}
          {/* Fixed-width so the panel doesn't jitter as the number changes. */}
          <span className="inline-block w-6 text-center font-semibold text-kc-navy tabular-nums"
            data-testid="relocation-countdown">
            {remaining}
          </span>
          …
        </p>

        <a
          href={status.url}
          className="inline-block mt-6 px-5 py-2.5 rounded-lg bg-kc-blue text-white text-sm font-medium hover:bg-kc-blue-dk"
        >
          Go there now
        </a>
      </div>
    </div>
  );
};

export default RelocationNotice;
